// src/pages/Pipeline.jsx
import Cargando from "../components/Cargando";
import React, { useState, useMemo, useEffect } from "react";
import { useCotizaciones } from "../hooks/useCotizaciones";

const ETAPAS_ACTIVAS = [
  { key: "Pendiente", color: "#378ADD", bg: "#EBF5FF", badgeBg: "#E6F1FB", badgeColor: "#185FA5" },
  { key: "Aprobada",  color: "#10B981", bg: "#F0FDF8", badgeBg: "#EAF3DE", badgeColor: "#3B6D11" },
  { key: "Ganada",    color: "#1D9E75", bg: "#F0FDF8", badgeBg: "#E1F5EE", badgeColor: "#0F6E56" },
];

const ETAPAS_TERMINAL = [
  { key: "Rechazada", color: "#DC2626", bg: "#FFF5F5", badgeBg: "#FCEBEB", badgeColor: "#A32D2D" },
  { key: "Vencida",   color: "#F97316", bg: "#FFF7ED", badgeBg: "#FAEEDA", badgeColor: "#854F0B" },
  { key: "Perdida",   color: "#DC2626", bg: "#FFF5F5", badgeBg: "#FCEBEB", badgeColor: "#A32D2D" },
];

const ETAPAS       = [...ETAPAS_ACTIVAS, ...ETAPAS_TERMINAL];
const ESTADOS_LISTA = ETAPAS.map(e => e.key);

export default function Pipeline() {
  const { cotizaciones, loading, actualizarCotizacion } = useCotizaciones();

  const [busqueda,          setBusqueda]          = useState("");
  const [filtroResponsable, setFiltroResponsable] = useState("todos");
  const [modalComent,       setModalComent]       = useState(null);
  const [comentario,        setComentario]        = useState("");
  const [pendingChange,     setPendingChange]     = useState(null);
  const [mounted,           setMounted]           = useState(false);

  useEffect(() => { const t = setTimeout(() => setMounted(true), 60); return () => clearTimeout(t); }, []);

  const responsables = useMemo(() => {
    const set = new Set(cotizaciones.map(c => c.responsable).filter(Boolean));
    return [...set].sort();
  }, [cotizaciones]);

  const cotFiltradas = useMemo(() => {
    let lista = [...cotizaciones];
    if (filtroResponsable !== "todos") lista = lista.filter(c => c.responsable === filtroResponsable);
    if (busqueda) {
      const b = busqueda.toLowerCase();
      lista = lista.filter(c =>
        c.clienteNombre?.toLowerCase().includes(b) ||
        c.clienteEmpresa?.toLowerCase().includes(b) ||
        c.proyecto?.toLowerCase().includes(b) ||
        c.numeroCot?.toLowerCase().includes(b)
      );
    }
    return lista;
  }, [cotizaciones, filtroResponsable, busqueda]);

  const handleCambiarEstado = (cot, nuevoEstado) => {
    if (nuevoEstado === cot.estado) return;
    if (nuevoEstado === "Ganada" || nuevoEstado === "Perdida") {
      setComentario("");
      setPendingChange({ id: cot.id, nuevoEstado, nombre: cot.clienteNombre, numeroCot: cot.numeroCot });
      setModalComent(true);
    } else {
      actualizarCotizacion(cot.id, { estado: nuevoEstado });
    }
  };

  const confirmarComent = async () => {
    if (!pendingChange) return;
    await actualizarCotizacion(pendingChange.id, { estado: pendingChange.nuevoEstado, comentarioHistorial: comentario });
    setModalComent(null); setComentario(""); setPendingChange(null);
  };

  if (loading) return (
    <Cargando />
  );

  const totalPipeline = cotFiltradas.filter(c => !["Perdida"].includes(c.estado)).reduce((s, c) => s + (Number(c.valor) || 0), 0);
  const totalCerrado  = cotFiltradas.filter(c => c.estado === "Ganada").reduce((s, c) => s + (Number(c.valor) || 0), 0);
  const conversion    = cotizaciones.length > 0 ? ((cotizaciones.filter(c => c.estado === "Ganada").length / cotizaciones.length) * 100).toFixed(1) : 0;

  const inp = { width: "100%", padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#1e293b", outline: "none", background: "#fff", fontFamily: "inherit", boxSizing: "border-box" };
  const lbl = { display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 5 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Modal comentario cierre/pérdida */}
      {modalComent && pendingChange && (
        <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="modal-content" style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 420, width: "90%" }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>
              {pendingChange.nuevoEstado === "Ganada" ? "🎉 Marcar como Ganada" : "❌ Marcar como Perdida"}
            </h3>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 14 }}>
              <strong>{pendingChange.nombre}</strong>
              {pendingChange.numeroCot && <> · <span style={{ color: "#3A3C7A" }}>{pendingChange.numeroCot}</span></>}
            </p>
            <label style={lbl}>{pendingChange.nuevoEstado === "Ganada" ? "¿Cómo se cerró?" : "¿Por qué se perdió?"}</label>
            <textarea value={comentario} onChange={e => setComentario(e.target.value)}
              placeholder={pendingChange.nuevoEstado === "Ganada" ? "Ej: Cliente firmó contrato..." : "Ej: Eligieron a la competencia..."}
              rows={3} style={{ ...inp, resize: "vertical", paddingTop: 8, paddingBottom: 8, marginBottom: 16 }} />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={confirmarComent} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: pendingChange.nuevoEstado === "Ganada" ? "#1D9E75" : "#DC2626", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Confirmar — {pendingChange.nuevoEstado}
              </button>
              <button onClick={() => { setModalComent(null); setPendingChange(null); }} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 13, cursor: "pointer", color: "#475569" }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b" }}>Embudo de ventas</h1>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            Pipeline: <strong style={{ color: "#1e293b" }}>S/ {totalPipeline.toLocaleString("es-PE")}</strong>
            {" · "}Cerrado: <strong style={{ color: "#1D9E75" }}>S/ {totalCerrado.toLocaleString("es-PE")}</strong>
            {" · "}Conversión: <strong style={{ color: Number(conversion) >= 30 ? "#1D9E75" : "#DC2626" }}>{conversion}%</strong>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            placeholder="Buscar cliente, proyecto, N°..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)}
            style={{ padding: "7px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, outline: "none", width: 220 }}
          />
          {responsables.length > 0 && (
            <select value={filtroResponsable} onChange={e => setFiltroResponsable(e.target.value)}
              style={{ padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, color: "#475569", background: "#fff", cursor: "pointer" }}>
              <option value="todos">Todos los asesores</option>
              {responsables.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* KPI chips por etapa */}
      <style>{`.pl-chip{transition:background 150ms cubic-bezier(0.23,1,0.32,1),box-shadow 150ms cubic-bezier(0.23,1,0.32,1),transform 100ms cubic-bezier(0.23,1,0.32,1)}.pl-chip:active{transform:scale(0.96)!important}.pl-chip:hover{filter:brightness(0.95)}`}</style>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>
        {ETAPAS.map(etapa => {
          const grupo = cotFiltradas.filter(c => c.estado === etapa.key);
          const valor = grupo.reduce((s, c) => s + (Number(c.valor) || 0), 0);
          const esTerminal = ETAPAS_TERMINAL.some(e => e.key === etapa.key);
          return (
            <div key={etapa.key} className="pl-chip kpi-card" style={{
              background: esTerminal ? etapa.bg : "#fff",
              border: `0.5px solid ${esTerminal ? etapa.color + "44" : "#e8ecf3"}`,
              borderTop: `3px solid ${etapa.color}`, borderRadius: 12, padding: "12px 10px",
              opacity: esTerminal && grupo.length === 0 ? 0.5 : 1,
            }}>
              <div style={{ fontSize: 9, color: "#64748b", marginBottom: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{etapa.key}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.5px" }}>{grupo.length}</div>
              <div style={{ fontSize: 9, color: "#64748b", marginTop: 3, fontWeight: 500 }}>S/ {(valor/1000).toFixed(0)}k</div>
            </div>
          );
        })}
      </div>

      {/* Barra visual del pipeline */}
      {(() => {
        const totalVal = ETAPAS.reduce((s, e) => s + cotFiltradas.filter(c => c.estado === e.key).reduce((a, c) => a + (Number(c.valor)||0), 0), 0) || 1;
        return (
          <div className="card-lift" style={{ background: "#fff", border: "0.5px solid #e8ecf3", borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 14 }}>Distribución del pipeline</div>
            <div style={{ display: "flex", borderRadius: 10, overflow: "hidden", height: 22, background: "#f1f5f9", gap: 1 }}>
              {ETAPAS.map((etapa, i) => {
                const val = cotFiltradas.filter(c => c.estado === etapa.key).reduce((s, c) => s + (Number(c.valor)||0), 0);
                const pct = (val / totalVal) * 100;
                if (pct < 0.5) return null;
                return (
                  <div key={etapa.key}
                    style={{
                      width: mounted ? `${pct}%` : "0%",
                      background: etapa.color,
                      transition: `width 700ms cubic-bezier(0.23,1,0.32,1) ${i * 80}ms`,
                      height: "100%",
                      cursor: "default",
                    }}
                    title={`${etapa.key}: S/ ${val.toLocaleString("es-PE")} (${pct.toFixed(1)}%)`}
                  />
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
              {ETAPAS.map(etapa => {
                const grupo = cotFiltradas.filter(c => c.estado === etapa.key);
                const val   = grupo.reduce((s, c) => s + (Number(c.valor)||0), 0);
                if (grupo.length === 0) return null;
                return (
                  <div key={etapa.key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: etapa.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 10, color: "#64748b" }}>{etapa.key}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#1e293b" }}>S/ {(val/1000).toFixed(0)}k</span>
                    <span style={{ fontSize: 10, color: "#94a3b8" }}>({grupo.length})</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Kanban — 3 columnas activas (Pendiente → Aprobada → Ganada) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        {ETAPAS_ACTIVAS.map(etapa => {
          const grupo      = cotFiltradas.filter(c => c.estado === etapa.key);
          const valorTotal = grupo.reduce((s, c) => s + (Number(c.valor) || 0), 0);
          return (
            <div key={etapa.key} style={{ borderRadius: 14, border: `1px solid ${etapa.color}22`, background: etapa.bg, display: "flex", flexDirection: "column", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ padding: "10px 14px", borderBottom: `0.5px solid ${etapa.color}22`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 10, background: etapa.badgeBg, color: etapa.badgeColor }}>
                  {etapa.key}
                </span>
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{grupo.length}</span>
                  <div style={{ fontSize: 9, color: "#64748b" }}>S/ {(valorTotal/1000).toFixed(0)}k</div>
                </div>
              </div>
              <div style={{ flex: 1, padding: 10, display: "flex", flexDirection: "column", gap: 8, overflowY: "auto", maxHeight: "60vh" }}>
                {grupo.length === 0 && (
                  <div style={{ textAlign: "center", fontSize: 11, color: "#cbd5e1", padding: "32px 0" }}>Sin cotizaciones</div>
                )}
                {grupo.map(c => (
                  <div key={c.id} className="kanban-card card-lift" style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #e2e8f0", padding: "10px 12px" }}>
                    {c.numeroCot && (
                      <span style={{ fontSize: 9, fontWeight: 700, color: "#3A3C7A", background: "#EEF2FF", padding: "1px 6px", borderRadius: 4, marginBottom: 6, display: "inline-block" }}>
                        {c.numeroCot}
                      </span>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#E6F1FB", color: "#185FA5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                        {(c.clienteNombre || "?").charAt(0)}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#1e293b", lineHeight: 1.2 }}>{c.clienteNombre}</div>
                    </div>
                    {c.clienteEmpresa && <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 2 }}>{c.clienteEmpresa}</div>}
                    {c.proyecto && <div style={{ fontSize: 10, color: "#64748b", marginBottom: 5, fontStyle: "italic" }}>{c.proyecto}</div>}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#1e293b" }}>S/ {Number(c.valor || 0).toLocaleString("es-PE")}</span>
                      {c.responsable && <span style={{ fontSize: 9, color: "#94a3b8" }}>{c.responsable.split(" ")[0]}</span>}
                    </div>
                    {c.proximoSeguimiento && (
                      <div style={{ fontSize: 9, color: "#64748b", marginTop: 3 }}>
                        📅 {(() => { const [y,m,d]=String(c.proximoSeguimiento).split("-").map(Number); return new Date(y,m-1,d).toLocaleDateString("es-PE"); })()}
                      </div>
                    )}
                    <div style={{ marginTop: 8, borderTop: "0.5px solid #f1f5f9", paddingTop: 7 }}>
                      <select value={c.estado} onChange={e => handleCambiarEstado(c, e.target.value)} onClick={e => e.stopPropagation()}
                        style={{ width: "100%", fontSize: 10, fontWeight: 600, padding: "3px 6px", borderRadius: 6, border: `1px solid ${etapa.color}`, background: etapa.badgeBg, color: etapa.badgeColor, cursor: "pointer", outline: "none" }}>
                        {ESTADOS_LISTA.map(e => <option key={e} value={e}>{e}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Estados terminales — compactos */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        {ETAPAS_TERMINAL.map(etapa => {
          const grupo = cotFiltradas.filter(c => c.estado === etapa.key);
          const valor = grupo.reduce((s, c) => s + (Number(c.valor) || 0), 0);
          return (
            <div key={etapa.key} style={{ background: etapa.bg, border: `0.5px solid ${etapa.color}33`, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 8, background: etapa.badgeBg, color: etapa.badgeColor }}>{etapa.key}</span>
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: "#1e293b" }}>{grupo.length}</span>
                  <span style={{ fontSize: 10, color: "#64748b", marginLeft: 4 }}>· S/ {(valor/1000).toFixed(0)}k</span>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 120, overflowY: "auto" }}>
                {grupo.slice(0, 5).map(c => (
                  <div key={c.id} style={{ background: "#fff", borderRadius: 6, padding: "5px 8px", display: "flex", justifyContent: "space-between", alignItems: "center", border: "0.5px solid #e2e8f0" }}>
                    <div style={{ fontSize: 10, color: "#475569", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "60%" }}>
                      {c.clienteNombre || "—"}
                      {c.numeroCot && <span style={{ fontSize: 9, color: "#94a3b8", marginLeft: 4 }}>{c.numeroCot}</span>}
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#64748b", flexShrink: 0 }}>S/ {(Number(c.valor||0)/1000).toFixed(0)}k</span>
                  </div>
                ))}
                {grupo.length > 5 && <div style={{ fontSize: 9, color: "#94a3b8", textAlign: "center" }}>+ {grupo.length - 5} más</div>}
                {grupo.length === 0 && <div style={{ fontSize: 10, color: "#cbd5e1", textAlign: "center", padding: "8px 0" }}>Sin registros</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
